import React from 'react';
import styled, { css } from 'styled-components';

export type ErrorMessageVariant = 'banner' | 'inline';

interface OwnProps {
  variant?: ErrorMessageVariant;
  /** Render the message in monospace (e.g. for parse errors). */
  monospace?: boolean;
  /** Additional inline style overrides — keep this minimal. */
  style?: React.CSSProperties;
  children: React.ReactNode;
}

const bannerStyles = css`
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.colors.dangerSurfaceStrong};
  border-left: 3px solid ${({ theme }) => theme.colors.statusFailed};
  border-radius: ${({ theme }) => theme.radii.sm};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.dangerBorderStrong};
  font-weight: 600;
`;

const inlineStyles = css`
  padding: 0.375rem 0.5rem;
  background: ${({ theme }) => theme.colors.dangerSurface};
  border-left: 3px solid ${({ theme }) => theme.colors.dangerText};
  border-radius: ${({ theme }) => theme.radii.sm};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.dangerText};
`;

const StyledAlert = styled.div<{ $variant: ErrorMessageVariant; $monospace: boolean }>`
  ${({ $variant }) => ($variant === 'banner' ? bannerStyles : inlineStyles)};
  ${({ $monospace }) => $monospace && 'font-family: monospace;'};
`;

/**
 * Standard error banner used across the app for surfaced errors:
 * load failures, validation errors, JSON parse errors, polling
 * failures, job errors.
 *
 * - `banner` is the full-width prominent style used on entry pages
 *   (WorkflowSelectorPage).
 * - `inline` is the compact style used inside panels / modals next
 *   to the related input.
 */
const ErrorMessage = ({ variant = 'inline', monospace = false, style, children }: OwnProps) => (
  <StyledAlert role='alert' $variant={variant} $monospace={monospace} style={style}>
    {children}
  </StyledAlert>
);

const MemoErrorMessage = React.memo(ErrorMessage);

export { MemoErrorMessage as ErrorMessage };
export default MemoErrorMessage;
